/**
 * Pre-canned TOU schedule templates for the Location TOU picker
 * (Commit C polish bundle, expanded set per the revised spec).
 *
 * Each preset captures a real utility's published schedule SHAPE
 * (which days/hours are peak / mid-peak / off-peak) along with
 * current placeholder rate values. The operator customizes after
 * loading; rates change quarterly for most US utilities and twice
 * yearly for Ontario, so the values here are starting points the
 * operator must verify against their actual rate sheet.
 *
 * Maintenance approach
 * --------------------
 * When a utility refreshes rates: update the affected preset's
 * rate values in place, bump its `last_verified` ISO date, and
 * commit. No other code changes needed — the picker reads these
 * values through presetWindowsForLoad below.
 *
 * WHOLE CENTS ONLY. The picker keeps a Location's TOU rates in whole
 * cents (Warren, 2026-09-27: its rate parser, parseTouRateText, rounds
 * what an operator types), so a published 9.8¢ goes in as 10. The Ontario
 * presets used to carry 9.8 / 20.3 / 15.7 / 3.9 / 39.1 verbatim and
 * loading one wrote fractional cents to the Location. They are rounded
 * here (half up), presetWindowsForLoad rounds again on load, and
 * touPresets.test.ts refuses a fractional rate in this file.
 *
 * Adding a new preset: append to TOU_PRESETS below. Every preset's
 * `windows` must use non-overlapping intervals within each day_of_week
 * the same constraint the server-side validator enforces. Day
 * numbering: 0 = Sun, 1 = Mon, ..., 6 = Sat.
 *
 * Sourcing references (for the maintainer who picks this up next):
 *   - Ontario RPP / ULO: https://oeb.ca (Ontario Energy Board)
 *   - PG&E E-TOU-C, EV2-A: pge.com tariff sheets
 *   - SCE TOU-D: sce.com/save-money/rates-financing/residential-rate-plans/time-of-use-plans
 *   - LADWP R-1B: ladwp.com Residential Electric Rates
 *
 * Specific PG&E and SCE rates below were sourced from aggregator
 * data; the next maintainer should cross-check against the
 * utility's current tariff sheet before publishing rate updates
 * to operators.
 *
 * Note on count: the revised spec described "17 entries" but the
 * enumerated list adds to 15. Shipped at 15 (the listed presets);
 * if the founder wants two more, append below + bump the section
 * with new rates.
 */

import type { ElectricityTouWindowAdmin } from '../components/LocationTouPicker';

export interface TouPreset {
  /** Stable id used in the selector. Not persisted. */
  id: string;
  display_name: string;
  /** Grouping bucket the picker uses to build <optgroup>s. */
  region: 'Generic' | 'Ontario' | 'California';
  /** Issuing utility, shown in the preset attribution callout. */
  utility: string;
  /** ISO date (YYYY-MM-DD) the preset's rates were last verified
   *  against the utility's published tariff. Surfaced in the
   *  "Verify rates" warning so operators know how stale the data is. */
  last_verified: string;
  /** Free-text effective-period note shown alongside the callout,
   *  e.g. "Effective May 1 - Oct 31, 2026". */
  notes: string;
  windows: ElectricityTouWindowAdmin[];
}

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKEND = [0, 6];

export const TOU_PRESETS: TouPreset[] = [
  // ── Generic ─────────────────────────────────────────────────────
  {
    id: 'flat',
    display_name: 'Flat rate',
    region: 'Generic',
    utility: 'No utility',
    last_verified: '2026-05-11',
    notes: 'Single rate all hours; baseline starting point.',
    windows: [
      { day_of_week: ALL_DAYS, start_hour: 0, end_hour: 24, rate_cents: 20, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'simple_peak_offpeak',
    display_name: 'Simple peak / off-peak',
    region: 'Generic',
    utility: 'Generic two-tier',
    last_verified: '2026-05-11',
    notes: 'Weekday 4 PM-9 PM peak; everything else off-peak.',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 16, end_hour: 21, rate_cents: 32, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 16, rate_cents: 18, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 21, end_hour: 24, rate_cents: 18, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 18, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'idle_fee_example',
    display_name: 'Per-hour idle fee (demo)',
    region: 'Generic',
    utility: 'Demo schedule',
    last_verified: '2026-05-11',
    notes: 'Mixes per-hour business-hour idle fee with per-kWh off-hours. Demonstrates the per-hour rate type.',
    windows: [
      // 60¢/hour, under the 80¢ per-hour cap (Warren, 2026-09-27), so the
      // demo loads clean. It was 100, which every load flagged for editing.
      { day_of_week: WEEKDAYS, start_hour: 9, end_hour: 17, rate_cents: 60, rate_type: 'per_hour' },
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 9, rate_cents: 18, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 17, end_hour: 24, rate_cents: 18, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 18, rate_type: 'per_kwh' },
    ],
  },

  // ── Ontario ─────────────────────────────────────────────────────
  {
    id: 'on_rpp_tou_winter',
    display_name: 'Ontario RPP TOU (Winter)',
    region: 'Ontario',
    utility: 'Ontario Energy Board — Regulated Price Plan',
    last_verified: '2026-05-11',
    notes: 'Effective Nov 1 - Apr 30. Winter peak shifts evening (5-7 PM).',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 7, rate_cents: 10, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 7, end_hour: 11, rate_cents: 20, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 11, end_hour: 17, rate_cents: 16, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 17, end_hour: 19, rate_cents: 20, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 19, end_hour: 24, rate_cents: 10, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 10, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'on_rpp_tou_summer',
    display_name: 'Ontario RPP TOU (Summer)',
    region: 'Ontario',
    utility: 'Ontario Energy Board — Regulated Price Plan',
    last_verified: '2026-05-11',
    notes: 'Effective May 1 - Oct 31. Summer peak shifts midday (11 AM-5 PM).',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 7, rate_cents: 10, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 7, end_hour: 11, rate_cents: 16, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 11, end_hour: 17, rate_cents: 20, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 17, end_hour: 19, rate_cents: 16, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 19, end_hour: 24, rate_cents: 10, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 10, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'on_ulo',
    display_name: 'Ontario Ultra-Low Overnight (ULO)',
    region: 'Ontario',
    utility: 'Ontario Energy Board — Ultra-Low Overnight',
    last_verified: '2026-05-11',
    notes: 'Year-round. Steep overnight discount; 4-9 PM weekday peak heavily penalized.',
    windows: [
      { day_of_week: ALL_DAYS, start_hour: 0, end_hour: 7, rate_cents: 4, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 23, end_hour: 24, rate_cents: 4, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 7, end_hour: 16, rate_cents: 16, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 16, end_hour: 21, rate_cents: 39, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 21, end_hour: 23, rate_cents: 16, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 7, end_hour: 23, rate_cents: 10, rate_type: 'per_kwh' },
    ],
  },

  // ── California — PG&E ───────────────────────────────────────────
  {
    id: 'pge_etou_c_summer',
    display_name: 'PG&E E-TOU-C (Summer)',
    region: 'California',
    utility: 'PG&E (Pacific Gas & Electric)',
    last_verified: '2026-05-11',
    notes: 'Effective Jun 1 - Sep 30. 4-9 PM peak daily incl. weekends. Verify against current tariff.',
    windows: [
      { day_of_week: ALL_DAYS, start_hour: 0, end_hour: 16, rate_cents: 41, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 16, end_hour: 21, rate_cents: 56, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 21, end_hour: 24, rate_cents: 41, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'pge_etou_c_winter',
    display_name: 'PG&E E-TOU-C (Winter)',
    region: 'California',
    utility: 'PG&E (Pacific Gas & Electric)',
    last_verified: '2026-05-11',
    notes: 'Effective Oct 1 - May 31. Same peak hours as summer; narrower peak/off-peak spread.',
    windows: [
      { day_of_week: ALL_DAYS, start_hour: 0, end_hour: 16, rate_cents: 39, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 16, end_hour: 21, rate_cents: 44, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 21, end_hour: 24, rate_cents: 39, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'pge_ev2a',
    display_name: 'PG&E EV2-A (EV-optimized)',
    region: 'California',
    utility: 'PG&E (Pacific Gas & Electric)',
    last_verified: '2026-05-11',
    notes: 'Year-round. EV-targeted rate with deep overnight off-peak (0-3 PM cheapest); 3-4 PM + 9 PM-midnight partial.',
    windows: [
      { day_of_week: ALL_DAYS, start_hour: 0, end_hour: 15, rate_cents: 27, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 15, end_hour: 16, rate_cents: 50, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 16, end_hour: 21, rate_cents: 62, rate_type: 'per_kwh' },
      { day_of_week: ALL_DAYS, start_hour: 21, end_hour: 24, rate_cents: 50, rate_type: 'per_kwh' },
    ],
  },

  // ── California — SCE ────────────────────────────────────────────
  {
    id: 'sce_toud_4_9pm_summer',
    display_name: 'SCE TOU-D-4-9PM (Summer)',
    region: 'California',
    utility: 'SCE (Southern California Edison)',
    last_verified: '2026-05-11',
    notes: 'Effective Jun-Sep. Weekday 4-9 PM on-peak; weekends entirely off-peak.',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 16, rate_cents: 41, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 16, end_hour: 21, rate_cents: 58, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 21, end_hour: 24, rate_cents: 41, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 41, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'sce_toud_4_9pm_winter',
    display_name: 'SCE TOU-D-4-9PM (Winter)',
    region: 'California',
    utility: 'SCE (Southern California Edison)',
    last_verified: '2026-05-11',
    notes: 'Effective Oct-May. Adds an 8 AM-4 PM mid-period off-peak tier; super off-peak before 8 AM + after 9 PM.',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 8, rate_cents: 33, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 8, end_hour: 16, rate_cents: 38, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 16, end_hour: 21, rate_cents: 44, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 21, end_hour: 24, rate_cents: 33, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 38, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'sce_toud_5_8pm_summer',
    display_name: 'SCE TOU-D-5-8PM (Summer)',
    region: 'California',
    utility: 'SCE (Southern California Edison)',
    last_verified: '2026-05-11',
    notes: 'Effective Jun-Sep. Narrower peak window (5-8 PM) at a steeper premium.',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 17, rate_cents: 41, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 17, end_hour: 20, rate_cents: 74, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 20, end_hour: 24, rate_cents: 41, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 41, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'sce_toud_5_8pm_winter',
    display_name: 'SCE TOU-D-5-8PM (Winter)',
    region: 'California',
    utility: 'SCE (Southern California Edison)',
    last_verified: '2026-05-11',
    notes: 'Effective Oct-May. Pre-8 AM + post-8 PM super off-peak; 8 AM-5 PM standard off-peak.',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 8, rate_cents: 33, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 8, end_hour: 17, rate_cents: 38, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 17, end_hour: 20, rate_cents: 49, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 20, end_hour: 24, rate_cents: 33, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 38, rate_type: 'per_kwh' },
    ],
  },

  // ── California — LADWP ──────────────────────────────────────────
  {
    id: 'ladwp_r1b_summer',
    display_name: 'LADWP R-1B (Summer)',
    region: 'California',
    utility: 'LADWP (Los Angeles Department of Water & Power)',
    last_verified: '2026-05-11',
    notes: 'Effective Jun 1 - Sep 30. Three-tier weekday (high / low / base); weekends base only.',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 10, rate_cents: 24, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 10, end_hour: 13, rate_cents: 27, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 13, end_hour: 17, rate_cents: 31, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 17, end_hour: 20, rate_cents: 27, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 20, end_hour: 24, rate_cents: 24, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 24, rate_type: 'per_kwh' },
    ],
  },
  {
    id: 'ladwp_r1b_winter',
    display_name: 'LADWP R-1B (Winter)',
    region: 'California',
    utility: 'LADWP (Los Angeles Department of Water & Power)',
    last_verified: '2026-05-11',
    notes: 'Effective Oct 1 - May 31. Compressed peak/base spread vs summer.',
    windows: [
      { day_of_week: WEEKDAYS, start_hour: 0, end_hour: 10, rate_cents: 20, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 10, end_hour: 13, rate_cents: 22, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 13, end_hour: 17, rate_cents: 24, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 17, end_hour: 20, rate_cents: 22, rate_type: 'per_kwh' },
      { day_of_week: WEEKDAYS, start_hour: 20, end_hour: 24, rate_cents: 20, rate_type: 'per_kwh' },
      { day_of_week: WEEKEND, start_hour: 0, end_hour: 24, rate_cents: 20, rate_type: 'per_kwh' },
    ],
  },
];

/** The windows a preset loads into the picker: a copy (the preset lives
 *  at module scope and the operator's edits must not mutate it) with
 *  every rate in whole cents. The data above is whole cents already;
 *  the Math.round keeps a fractional rate added later from ever reaching
 *  a Location. */
export function presetWindowsForLoad(preset: TouPreset): ElectricityTouWindowAdmin[] {
  return preset.windows.map((w) => ({
    ...w,
    day_of_week: [...w.day_of_week],
    rate_cents: Math.round(w.rate_cents),
  }));
}

/** Group order for the picker's <optgroup> rendering. Generic first
 *  (catch-all + demos), then Ontario (founder's territory), then
 *  California (US launch focus). New regions append. */
export const TOU_PRESET_REGIONS: TouPreset['region'][] = ['Generic', 'Ontario', 'California'];
